// User Group Manager
// Manage user groups for task assignment

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserGroups } from '../../../hooks/useUserGroups';
import { useGroupTypes } from '../../../hooks/useTaskNomenclatures';
import UserGroupList from './UserGroupList';
import UserGroupForm from './UserGroupForm';
import './UserGroupManager.css';

const UserGroupManager = ({ userId }) => {
  const { t } = useTranslation('userManagement');
  const [activeView, setActiveView] = useState('list'); // 'list' or 'form'
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [filters, setFilters] = useState({
    searchTerm: '',
    groupType: '',
    departmentId: '',
    isActive: true
  });

  const { groups, loading, error, refresh } = useUserGroups(filters);
  const { items: groupTypes } = useGroupTypes();

  const handleCreateGroup = () => {
    setSelectedGroup(null);
    setActiveView('form');
  };

  const handleEditGroup = (group) => {
    setSelectedGroup(group);
    setActiveView('form');
  };

  const handleFormSave = () => {
    setActiveView('list');
    setSelectedGroup(null);
    refresh();
  };

  const handleFormCancel = () => {
    setActiveView('list');
    setSelectedGroup(null);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (activeView === 'form') {
    return (
      <UserGroupForm
        group={selectedGroup}
        userId={userId}
        onSave={handleFormSave}
        onCancel={handleFormCancel}
      />
    );
  }

  return (
    <div className="user-group-manager">
      <div className="group-manager-header">
        <div>
          <h2>{t('userGroupsTitle')}</h2>
          <p className="subtitle">{t('userGroupsSubtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreateGroup}>
          {t('createGroup')}
        </button>
      </div>

      {/* Filters */}
      <div className="group-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search groups..."
            value={filters.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <select
            value={filters.groupType}
            onChange={(e) => handleFilterChange('groupType', e.target.value)}
            className="filter-select"
          >
            <option value="">{t('allGroupTypes')}</option>
            {groupTypes && groupTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filters.isActive ? 'active' : 'inactive'}
            onChange={(e) => handleFilterChange('isActive', e.target.value === 'active')}
            className="filter-select"
          >
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
            <option value="">All Groups</option>
          </select>
        </div>
      </div>

      {/* Group List */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('loadingGroups')}</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{t('errorLoadingGroups')} {error}</p>
          <button onClick={refresh} className="btn btn-secondary">{t('retry')}</button>
        </div>
      ) : (
        <UserGroupList
          groups={groups}
          onEdit={handleEditGroup}
          onRefresh={refresh}
          userId={userId}
        />
      )}
    </div>
  );
};

export default UserGroupManager;


